import React, { FC, useState } from 'react';
import {
	alephToast,
	BlurModal,
	Button,
	ButtonAppearance,
	ButtonColor,
	CheckIcon,
	Floaty,
	Input,
	LoaderBars,
	ModalHeader,
	NotificationToast,
	NotificationType,
	SwitchInput,
	SwitchSize,
	UserIcon,
	isReddit,
	userPasswordValidator,
	AvatarEntity,
	isSpotify,
} from '@aleph/shared-components';
import ProfileImageUploader from 'wb-reddit/app/common/components/profileImageUploader';
import { useFormik } from 'formik';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { object, ref, string } from 'yup';
import { gql, useMutation } from '@apollo/client';
import { UpdateMeDocument } from 'wb-reddit/graphql/generated';
import TwoFactorModal, { TwoFactorSteps } from 'wb-reddit/app/common/components/twoFactor/twoFactorModal';
import { useTranslation } from 'react-i18next';
import { captureException } from '@sentry/react';

type UpdateSelfOverlayProps = {
	onClose: () => void
};

const authGraphQuery = gql`
	mutation authGraphUpdateMe(
		$userName: String
		$userSurname: String
		$userPassword: String
	) {
		updateMe(
			userName: $userName
			userSurname: $userSurname
			userPassword: $userPassword
		) {
			id: userId
			userName
			userSurname
		}
	}

`;

const UpdateSelfOverlay: FC<UpdateSelfOverlayProps> = ({ onClose }) => {
	const { t } = useTranslation();
	const [doUpdate, { loading }] = useMutation(isReddit() ? UpdateMeDocument : authGraphQuery);

	const [twoFactorModalOpen, setTwoFactorModalOpen] = useState(false);

	const platformUser = useUserMetaData();

	const [hasTwoFactor, setHasTwoFactor] = useState(!!platformUser?.userTwoFactorAuthenticationEnabled);

	const {
		values,
		errors,
		setFieldTouched,
		setFieldValue,
		touched,
		handleChange,
		handleBlur,
		handleSubmit,
	} = useFormik({
		initialValues: {
			userName:                 platformUser?.userName ?? '',
			userSurname:              platformUser?.userSurname ?? '',
			userEmail:                platformUser?.userEmail ?? '',
			userProfilePicture:       platformUser?.userProfilePictureUrl ?? undefined,
			userPassword:             '',
			userPasswordConfirmation: '',
		},
		validationSchema: object().shape({
			userName:                 string().required(t('re:user first name required', 'First name is required')),
			userSurname:              string().required(t('re:user last name required', 'Last name is required')),
			userPassword:             userPasswordValidator,
			userPasswordConfirmation: string().oneOf(
				[ref('userPassword')],
				t('sc: auth.pass.match', 'Passwords must match'),
			),
		}),
		onSubmit: async (submitValues) => {
			try {
				await doUpdate({
					variables: {
						userName:                 submitValues.userName,
						userSurname:              submitValues.userSurname,
						userPassword:             submitValues.userPassword ? submitValues.userPassword : undefined,
						userPasswordConfirmation: submitValues.userPasswordConfirmation
							? submitValues.userPasswordConfirmation
							: undefined,
						userProfilePicture: (
							values?.userProfilePicture as unknown as { file: File }
						)?.file ?? undefined,
					},
				});
			} catch (e) {
				captureException(e);

				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={t('sc: action err', { action: 'saving your new information' })}
					/>,
				);

				return;
			}

			onClose();

			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					title={t('re: user_update.success', 'You have successfully updated your information.')}
				/>,
			);
		},
	});

	const onUploadProfileImage = (newImage) => {
		setFieldValue('userProfilePicture', newImage);
		setFieldTouched('userProfilePicture');
	};

	const onTwoFactorClose = (step: TwoFactorSteps) => {
		if (step === 'authCode') {
			setHasTwoFactor(curr => !curr);
		}
		setTwoFactorModalOpen(false);
	};

	return (
		<BlurModal
			blur
			header={
				<ModalHeader
					icon={
						<UserIcon
							width={32}
							height={32}
						/>
					}
					title={t('re: user_update.title', 'Your profile')}
					onClose={onClose}
				/>
			}
		>
			<div className="flex flex-1 min-w-full px-[40px] pb-0 pt-[40px] gap-[40px]">
				<div className="flex-1 w-[25%] flex justify-center">
					<ProfileImageUploader
						profileImage={values?.userProfilePicture ?? undefined}
						onUploadProfileImage={onUploadProfileImage}
						type="User"
						avatarEntity={AvatarEntity.User}
						readOnly={isSpotify()}
					/>
				</div>

				<div className="flex-[2] min-w-[50%] w-[50%] flex flex-col gap-[16px]
					px-[2px] pb-[150px]"
				>
					<div className="font-semibold text-[14px]">GENERAL INFORMATION</div>
					<div className="flex gap-[16px]">
						<Input
							className="flex-1"
							id="userName"
							label={t('re:user first name', 'First name')}
							onChange={handleChange}
							onBlur={handleBlur}
							// @ts-ignore
							validationError={touched.userName ? errors.userName : undefined}
							value={values.userName ?? ''}
							disabled={loading}
						/>

						<Input
							className="flex-1"
							id="userSurname"
							label={t('re:user last name', 'Last name')}
							onChange={handleChange}
							onBlur={handleBlur}
							// @ts-ignore
							validationError={touched.userSurname ? errors.userSurname : undefined}
							value={values?.userSurname ?? ''}
							disabled={loading}
						/>
					</div>

					<div>
						<Input
							className="flex-1"
							id="userEmail"
							type="email"
							label={t('re: email', 'E-Mail')}
							disabled
							value={values?.userEmail ?? ''}
						/>
					</div>

					<div className="flex gap-[16px]">
						<Input
							className="flex-1"
							id="userPassword"
							type="password"
							label={t('sc: auth.password', 'Password')}
							autoComplete="off"
							onChange={handleChange}
							value={values?.userPassword ?? ''}
							validationError={(touched.userPassword || touched.userPasswordConfirmation)
								? errors.userPassword
								: undefined}
							onBlur={handleBlur}
							disabled={loading}
							data-testid="password-input"
						/>

						<Input
							className="flex-1"
							id="userPasswordConfirmation"
							type="password"
							label={t('sc: auth.repeat_password', 'Repeat password')}
							autoComplete="off"
							onChange={handleChange}
							value={values?.userPasswordConfirmation ?? ''}
							validationError={(touched.userPassword || touched.userPasswordConfirmation)
								? errors.userPasswordConfirmation
								: undefined}
							onBlur={handleBlur}
							disabled={loading}
							data-testid="confirm-password-input"

						/>
					</div>
					<div className='flex flex-row items-center text-gray-800 gap-3 text-[13px]'>
						<SwitchInput
							checked={!!hasTwoFactor}
							size={SwitchSize.LARGE}
							onClick={async () => {
								setTwoFactorModalOpen(true);
								setHasTwoFactor(curr => !curr);
							}}
							data-testid="two-factor-button"
						/>
						<p>
							{t('sc: auth.tfa.enable_tfa', 'Enable two factor authentication')}
						</p>
					</div>
				</div>

				<div className="flex-1 w-[25%] flex flex-col gap-[16px]" />
			</div>

			<Floaty>
				{
					!loading
					&& <Button
						title={t('sc: discard', 'Discard')}
						data-testid="overlay-discard"
						appearance={ButtonAppearance.OUTLINED}
						color={ButtonColor.RED}
						onClick={onClose}
					/>
				}

				<Button
					title={t('sc: confirm', 'Confirm')}
					data-testid="overlay-confirm"
					onClick={() => handleSubmit()}
					leftNode={loading ? <LoaderBars className="animate-spin" /> : <CheckIcon />}
					disabled={!Object.values(touched)?.length || !!Object.values(errors)?.length}
					color={ButtonColor.WISE_BLUE}
					loading={loading}
				/>
			</Floaty>
			{twoFactorModalOpen && <TwoFactorModal
				email={platformUser?.userEmail ?? ''}
				type={hasTwoFactor ? 'enable' : 'disable'}
				onClose={onTwoFactorClose}
			/>}
		</BlurModal>
	);
};

export default UpdateSelfOverlay;
